import { C2paProvider } from "@contentauth/react";
import wasmSrc from "c2pa/dist/assets/wasm/toolkit_bg.wasm?url";
import workerSrc from "c2pa/dist/c2pa.worker.min.js?url";
import i18n from "i18next";
import { merge } from "lodash";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { LaunchDarklyProvider } from "./components/common/LaunchDarklyProvider/LaunchDarklyProvider.tsx";
import { PostHogProvider } from "./components/common/PostHogProvider/PostHogProvider";
import { ENV_VARS } from "./config/env.ts";
import { ThemeModeProvider } from "./context/ThemeContext";
import "./index.module.scss";
import BriaThemeProvider from "./mobx/BriaThemeProvider";
import MobxProvider from "./mobx/store-provider";
import iframeStore from "./pages/IframeNew/iframe-store.tsx";
import { isMigrationIframe } from "./utils/migrationIframe.ts";

const loadTranslationFiles = (languageModules: any) => {
	const files = Object.values(languageModules);
	return files.reduce((acc, curr) => merge(acc, curr), {}) as any;
};

const enModules = import.meta.glob("./translations/en/**/*.json", { eager: true });

const createNamespaces = (modules: any) => {
	const namespaces: any = {};

	Object.keys(modules).forEach((path) => {
		const fileName = path.split("/").pop()?.replace(".json", "");
		if (fileName) {
			namespaces[fileName] = modules[path];
		}
	});

	return namespaces;
};

const enNamespaces = createNamespaces(enModules);

i18n.init({
	resources: {
		en: {
			translation: loadTranslationFiles(enModules),
			...enNamespaces,
		},
		// Add translations for other languages here
	},
	lng: "en", // Set the default language
	fallbackLng: "en",
	interpolation: {
		escapeValue: false, // Allows using HTML in translations
	},
	nsSeparator: false,
});

const AppComponents = () => (
	<I18nextProvider i18n={i18n}>
		<ThemeModeProvider>
			<BrowserRouter basename={ENV_VARS.VITE_BASE_DIR || "/"}>
				<BriaThemeProvider>
					<App />
				</BriaThemeProvider>
			</BrowserRouter>
		</ThemeModeProvider>
	</I18nextProvider>
);

// Redirect direct visits (not in iframe, not embed=migration) to platform-frontend /console
function redirectDirectVisitToPlatformFrontend(): boolean {
	if (typeof window === "undefined" || iframeStore.isIframe() || isMigrationIframe()) return false;
	const frontendUrl = ENV_VARS.VITE_NEW_PLATFORM_URL;
	if (!frontendUrl) return false;
	const pathname = window.location.pathname || "/";
	const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
	// Don't redirect labs; let them render in platform client
	if (
		normalizedPath.includes("labs") ||
		normalizedPath.includes("playground-hub") ||
		normalizedPath.includes("apps") ||
		normalizedPath.includes("pricing") ||
		normalizedPath.includes("automotive")
	)
		return false;
	const base = frontendUrl.replace(/\/$/, "");
	// Playground-hub should open in iframe route (/playground-hub), not under /console
	const target = normalizedPath === "/" ? `${base}/console` : `${base}/console${normalizedPath}`;
	window.location.replace(target);
	return true;
}

if (!redirectDirectVisitToPlatformFrontend()) {
	ReactDOM.createRoot(document.getElementById("root")!).render(
		<PostHogProvider>
			<MobxProvider>
				<LaunchDarklyProvider>
					{iframeStore.isIframe() ? (
						<AppComponents />
					) : (
						<C2paProvider
							config={{
								wasmSrc,
								workerSrc,
							}}
						>
							<AppComponents />
						</C2paProvider>
					)}
				</LaunchDarklyProvider>
			</MobxProvider>
		</PostHogProvider>,
	);
}
